import { Controller } from "@hotwired/stimulus";
import { visit } from "@hotwired/turbo";
import { useClickOutside } from "stimulus-use";

export default class extends Controller {
  static targets = ["input", "results", "loading", "shortcutContainer"];
  static values = {
    minLength: Number,
    debounceMs: { type: Number, default: 300 },
    // When true, results stay visible (full-page search) instead of hiding on
    // blur / click-outside like the floating dropdown.
    persist: { type: Boolean, default: false },
  };
  static classes = ["selected"];

  initialize() {
    this.debouncedPerformSearch = this.debounce(
      this.performSearch.bind(this),
      this.debounceMsValue || 300,
    );
    this.selectedIndex = -1;
  }

  connect() {
    // Apply useClickOutside with correct configuration
    useClickOutside(this, {
      element: this.element,
    });

    // Set proper ARIA attributes
    this.inputTarget.setAttribute("role", "combobox");
    this.inputTarget.setAttribute("aria-autocomplete", "list");
    this.inputTarget.setAttribute(
      "aria-controls",
      this.resultsTarget.id || "search-results",
    );
    this.inputTarget.setAttribute("aria-expanded", "false");

    // Make sure the shortcut container is visible initially
    if (this.hasShortcutContainerTarget) {
      this.shortcutContainerTarget.classList.remove("hidden");
    }

    // Set up MutationObserver to watch for content changes
    this.setupMutationObserver();

    // Add blur event listener to handle mobile tap outside
    this.blurHandler = this.handleBlur.bind(this);
    this.inputTarget.addEventListener("blur", this.blurHandler);

    // Add click event listener to results to hide immediately when clicking links
    this.resultsClickHandler = this.handleResultsClick.bind(this);
    this.resultsTarget.addEventListener("click", this.resultsClickHandler);
  }

  disconnect() {
    // Clean up event listeners
    if (this.blurHandler) {
      this.inputTarget.removeEventListener("blur", this.blurHandler);
    }

    if (this.resultsClickHandler) {
      this.resultsTarget.removeEventListener("click", this.resultsClickHandler);
    }

    // Disconnect the observer when controller disconnects
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  setupMutationObserver() {
    this.observer = new MutationObserver((mutations) => {
      if (
        this.resultsTarget.innerHTML.trim() &&
        !this.resultsTarget.querySelector(".empty-results")
      ) {
        setTimeout(() => {
          this.showResults();
          this.selectedIndex = -1;
        }, 0);
      }
    });

    this.observer.observe(this.resultsTarget, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  search(event) {
    const query = event.target.value.trim();

    if (query.length === 0) {
      this.clearResults();
      return;
    }

    if (query.length >= this.minLengthValue) {
      this.showLoading();
      // This is the key fix - call the debounced function but don't use another function in between
      this.debouncedPerformSearch();
    }
  }

  handleClear(event) {
    // Handle native search field clear button
    if (event.target.value === "") {
      this.clearResults();
    }
  }

  async performSearch() {
    try {
      const query = this.inputTarget.value.trim();

      if (query.length < this.minLengthValue) {
        this.hideLoading();
        return;
      }

      // Submit the form to get search results
      await this.element.requestSubmit();

      // Directly check if results are present after form submission
      setTimeout(() => {
        if (this.resultsTarget.innerHTML.trim()) {
          this.showResults();
        }
      }, 50); // Small delay to allow Turbo to update the DOM
    } catch (error) {
      console.error("Search failed:", error);
    } finally {
      this.hideLoading();
    }
  }

  // Improved debounce implementation
  debounce(func, wait) {
    let timeout;
    return () => {
      const context = this;
      const args = arguments;

      const later = () => {
        timeout = null;
        func.apply(context, args);
      };

      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  navigate(event) {
    // Enter is handled independently of the current results so it works even
    // before the debounced results have loaded.
    if (event.key === "Enter") {
      event.preventDefault();
      if (this.selectedIndex >= 0) {
        this.visitSelectedResult();
      } else {
        this.visitAllResults();
      }
      return;
    }

    const results = this.resultsTarget.querySelectorAll('[role="option"]');
    if (!results.length) return;

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        if (this.selectedIndex === -1) {
          this.selectedIndex = 0;
        } else {
          this.selectedIndex = Math.min(
            this.selectedIndex + 1,
            results.length - 1,
          );
        }
        this.highlightResult();
        break;
      case "ArrowUp":
        event.preventDefault();
        this.selectedIndex = Math.max(this.selectedIndex - 1, -1);
        this.highlightResult();
        break;
      case "Escape":
        event.preventDefault();
        this.inputTarget.value = "";
        this.clearResults();
        this.inputTarget.blur();
        break;
      case "Tab":
        this.hideResults();
        break;
    }
  }

  visitSelectedResult() {
    const results = this.resultsTarget.querySelectorAll('[role="option"]');
    const selectedResult = results[this.selectedIndex];
    if (selectedResult) {
      const link = selectedResult.querySelector("a");
      if (link) {
        // Using Turbo visit for navigation
        visit(link.href);
        this.hideResults();
      }
    }
  }

  // Enter with no result highlighted: navigate to the full-page search
  // results. Derives the HTML URL from the form's own turbo_stream action so
  // the route stays in one place.
  visitAllResults() {
    const query = this.inputTarget.value.trim();
    if (query.length < this.minLengthValue) return;

    const url = new URL(this.element.action);
    url.pathname = url.pathname.replace(/\.turbo_stream$/, "");
    url.searchParams.set(this.inputTarget.name, query);
    visit(url.toString());
    this.hideResults();
  }

  highlightResult() {
    const results = this.resultsTarget.querySelectorAll('[role="option"]');
    results.forEach((result, index) => {
      result.setAttribute("aria-selected", index === this.selectedIndex);

      if (index === this.selectedIndex) {
        result.scrollIntoView({ block: "nearest" });
      }
    });
  }

  clearResults() {
    if (this.hasResultsTarget) {
      this.resultsTarget.innerHTML = "";
      this.hideResults();
    }
    this.selectedIndex = -1;
    this.inputTarget.setAttribute("aria-expanded", "false");
  }

  handleBlur(event) {
    // Use setTimeout to allow for potential clicks on search results
    setTimeout(() => {
      // Check if focus moved to something inside the search results
      if (!this.element.contains(document.activeElement)) {
        this.hideResults();
      }
    }, 100);
  }

  handleResultsClick(event) {
    // Hide results immediately when clicking on any link in results
    if (event.target.tagName === "A" || event.target.closest("a")) {
      this.hideResults();
    }
  }

  handleCancel(event) {
    // Clear the search input and hide results
    this.inputTarget.value = "";
    this.clearResults();
    this.inputTarget.blur();
  }

  // This method is called by useClickOutside
  clickOutside(event) {
    this.hideResults();
  }

  hideResults() {
    if (this.persistValue) return;

    if (this.hasResultsTarget) {
      this.resultsTarget.classList.add("opacity-0", "invisible");
      this.inputTarget.setAttribute("aria-expanded", "false");
    }
  }

  showResults() {
    if (this.hasResultsTarget) {
      this.resultsTarget.classList.remove("opacity-0", "invisible");
      this.inputTarget.setAttribute("aria-expanded", "true");
    }
  }

  showLoading() {
    this.loadingTarget.classList.remove("hidden");
    this.loadingTarget.classList.add("flex");

    // Hide keyboard shortcut
    if (this.hasShortcutContainerTarget) {
      this.shortcutContainerTarget.classList.add("hidden");
    }
  }

  hideLoading() {
    this.loadingTarget.classList.remove("flex");
    this.loadingTarget.classList.add("hidden");

    // Show keyboard shortcut container
    if (this.hasShortcutContainerTarget) {
      this.shortcutContainerTarget.classList.remove("hidden");
    }
  }
}
